import { BE_BASE_URL } from "@lib/constants/AppConst";
import redux from "@lib/redux";
import { logout } from "@lib/redux/slices/auth";
import axios, { AxiosError } from "axios";
import { toast } from "react-toastify";
import { showErrorToast } from "@lib/helpers/ToastHelper";

export const MockAPI = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
  },
});

export const API = axios.create({
  baseURL: BE_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
  },
  timeout: 30000,
});

API.interceptors.request.use(
  (config) => {
    const { accessToken } = redux.getState().auth?.user || {};
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error: AxiosError<any>) => {
    const errMessage = error.response?.data?.message;
    if (errMessage) {
      showErrorToast(errMessage);
    }
    return Promise.reject(error);
  },
);

API.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<any>) => {
    let errMessage = error.message || "Error";
    // request happened and server responded
    if (error.response) {
      errMessage = error?.response?.data?.message?.toString();

      if (errMessage.includes("TwoFactorAuthenticationRequired")) {
        return Promise.reject(error.response.data);
      }

      const UnauthorizedMessages = [
        "Unauthorized",
        "Login from another location",
        "Access token not found",
        "User has been distributed to access the system",
      ];

      if (
        error.response.status === 401 &&
        UnauthorizedMessages.includes(error.response?.data?.message)
      ) {
        if (!error.response.config.url.startsWith("/auth")) {
          errMessage = "Oops! Your session has expired. Please login again.";
          if (error.response?.data?.message !== UnauthorizedMessages[0]) {
            errMessage = error.response?.data?.message;
          }

          redux.dispatch(logout());
          toast.dismiss();

          showErrorToast(errMessage, { autoClose: 10000 });
          return Promise.reject(error.response.data);
        }
      }

      if (error.response.status === 500) {
        errMessage = "Internal Server Error";
        if (error.response?.data?.message) {
          errMessage = error.response?.data?.message;
        }
      }
      if (error.response.status === 508) {
        errMessage = "Time Out";
      }
      if (error.response.status === 429) {
        errMessage = "Too Many Requests";
      }

      showErrorToast(errMessage, { autoClose: 10000 });
      return Promise.reject(error.response.data);
    }

    if (error?.response?.status === 504 || error?.code === "ECONNABORTED") {
      errMessage = "Gateway Timeout";
    }
    // request is made but no response received or request triggered an error
    showErrorToast(errMessage);
    return Promise.reject(error);
  },
);
